/**
 * Frame → printer routing (Phase 1 of docs/printer-frame-routing-plan.md).
 *
 * A printer slot holds a set of CLAIMS (everything / specific frames /
 * categories); routing is the resolution of all claims over one frame. This
 * module is deliberately pure — no zustand, no window — so the equivalence
 * suite can prove the central promise of Phase 1:
 *
 *   With multi-claim routing DISABLED (the default), resolvePrintPlan returns
 *   byte-identically what the legacy getPrinterForFrame returned: tertiary
 *   frameIds first, then secondary, then the default printer.
 *
 * Round-robin over shared claims — the new behaviour — only activates when the
 * caller passes `multiClaimRouting: true`, which the store wires to the
 * Studio-resolved feature flag `printer_multi_claim_routing`. Flag off, the
 * fleet cannot observe any change.
 */

import type { PrintSettings } from '@renderer/stores/printStore'

export type SlotId = 'default' | 'secondary' | 'tertiary'

/**
 * A category claim. The booth's only handle on a category is its display NAME
 * (the frames API drops the subdocument _id), and the live category catalog is
 * in-memory and network-only — so a claim carries a SNAPSHOT of the resolved
 * frame ids beside the name. The name is the binding; the snapshot is what
 * routing uses whenever the live catalog is unavailable (cold boot, offline,
 * category mode switched off in Studio).
 */
export interface CategoryClaim {
  name: string
  /** Refreshed on every successful catalog load. Never cleared on a failed load. */
  frameIdsSnapshot: string[]
  /** Epoch ms of the last successful resolve. Absent => never resolved. */
  resolvedAt?: number
}

/** What a slot claims. Absent on a slot => that slot claims nothing. */
export interface PrinterRouting {
  /** true => claims every frame. The catch-all. The default slot migrates to true. */
  allFrames: boolean
  /** Explicitly ticked frames. Ignored when allFrames is true. */
  frameIds: string[]
  /** MANY categories per slot, by design. Ignored when allFrames is true. */
  categories: CategoryClaim[]
}

/** One slot as the resolver sees it. Undefined printerName => slot not configured. */
export interface RoutingSlot {
  slotId: SlotId
  printerName?: string
  settings: PrintSettings
  routing?: PrinterRouting
}

/** A live category from configStore (name + current frame ids). */
export interface LiveCategory {
  name: string
  ids: string[]
}

export interface PrintTarget {
  slotId: SlotId
  printerName: string
  settings: PrintSettings
  /**
   * How the frame matched — telemetry + operator explanation. `project` = no
   * slot named this frame, but the active project is bound to this slot
   * (multi-project hub: "each mode prints on its own printer" as one radio).
   */
  match: 'frame' | 'category' | 'project' | 'all' | 'fallback'
  /** True when this slot won a round-robin against other claimants. */
  shared: boolean
  categoryName?: string
}

export type RoutingWarning =
  | { kind: 'unclaimed-frame' }
  | { kind: 'missing-category'; name: string }
  | { kind: 'duplicate-queue-name'; printerName: string; slotIds: SlotId[] }

export interface PrintPlan {
  frameId: string
  /** Always present — a paid print is never dropped. */
  target: PrintTarget
  /** Every slot that claimed this frame, before resolution. Drives the UI badge. */
  claimants: SlotId[]
  /** Config problems. Surfaced in Settings; NEVER blocks a print. */
  warnings: RoutingWarning[]
}

export interface ResolveOptions {
  /**
   * The `printer_multi_claim_routing` feature flag. false (default) => overlap
   * resolves by the legacy strict priority (tertiary > secondary > default),
   * which is byte-identical to the pre-Phase-1 getPrinterForFrame — including
   * for configs that already have one frame in two slots' lists. true =>
   * overlapping specific claims share via round-robin.
   */
  multiClaimRouting?: boolean
  /** Round-robin cursors per sharer group. Only read/advanced when sharing. */
  cursor?: Record<string, number>
  /**
   * Slot the ACTIVE project is bound to (`printStore.printerSlotByProject`).
   * Resolved BELOW frame/category claims and ABOVE the allFrames catch-all:
   * a frame nobody named prints on the project's printer, a frame someone did
   * name keeps going where it always went. Undefined => byte-identical to
   * the pre-binding resolver (equivalence suite).
   */
  projectSlot?: SlotId
}

export interface ResolveResult {
  plan: PrintPlan
  /** Unchanged unless a round-robin actually advanced. */
  nextCursor: Record<string, number>
}

/** Legacy priority, and the resolution order inside a sharer group. */
const SLOT_PRIORITY: SlotId[] = ['tertiary', 'secondary', 'default']

interface Claim {
  slot: RoutingSlot
  match: 'frame' | 'category' | 'all'
  categoryName?: string
}

/**
 * Resolve one category claim to frame ids: live catalog first, snapshot as the
 * offline path. A missing category is a warning, never an error.
 */
function categoryFrameIds(
  claim: CategoryClaim,
  liveCategories: LiveCategory[],
  warnings: RoutingWarning[]
): string[] {
  const live = liveCategories.find((c) => c.name === claim.name)
  if (live) return live.ids
  if (!warnings.some((w) => w.kind === 'missing-category' && w.name === claim.name)) {
    warnings.push({ kind: 'missing-category', name: claim.name })
  }
  return claim.frameIdsSnapshot
}

function claimOf(
  slot: RoutingSlot,
  frameId: string,
  liveCategories: LiveCategory[],
  warnings: RoutingWarning[]
): Claim | null {
  if (!slot.printerName) return null
  const routing = slot.routing
  if (!routing) {
    // Unmigrated/absent routing on the default slot means catch-all (legacy
    // behaviour); on secondary/tertiary it means "claims nothing".
    return slot.slotId === 'default' ? { slot, match: 'all' } : null
  }
  if (routing.allFrames) return { slot, match: 'all' }
  if (routing.frameIds.includes(frameId)) return { slot, match: 'frame' }
  for (const cat of routing.categories) {
    if (categoryFrameIds(cat, liveCategories, warnings).includes(frameId)) {
      return { slot, match: 'category', categoryName: cat.name }
    }
  }
  return null
}

function toTarget(claim: Claim, shared: boolean): PrintTarget {
  return {
    slotId: claim.slot.slotId,
    printerName: claim.slot.printerName as string,
    settings: claim.slot.settings,
    match: claim.match,
    shared,
    categoryName: claim.categoryName
  }
}

/**
 * The routing decision for one frame. Pure: same inputs, same plan (the cursor
 * advance is returned, not written anywhere).
 */
export function resolvePrintPlan(
  frameId: string,
  slots: RoutingSlot[],
  liveCategories: LiveCategory[],
  options: ResolveOptions = {}
): ResolveResult {
  const warnings: RoutingWarning[] = []
  const cursor = options.cursor ?? {}

  const ordered = SLOT_PRIORITY.map((id) => slots.find((s) => s.slotId === id)).filter(
    (s): s is RoutingSlot => !!s
  )
  const defaultSlot = ordered.find((s) => s.slotId === 'default')

  const claims = ordered
    .map((slot) => claimOf(slot, frameId, liveCategories, warnings))
    .filter((c): c is Claim => c !== null)

  // Two slots naming the same Windows queue are one physical printer; keep the
  // higher-priority claim so a future fan-out cannot double-print on it, and
  // surface the config problem.
  const seenQueues = new Map<string, SlotId[]>()
  const deduped: Claim[] = []
  for (const claim of claims) {
    const queue = (claim.slot.printerName as string).trim().toLowerCase()
    const holders = seenQueues.get(queue)
    if (holders) {
      holders.push(claim.slot.slotId)
    } else {
      seenQueues.set(queue, [claim.slot.slotId])
      deduped.push(claim)
    }
  }
  for (const [queue, holders] of seenQueues) {
    if (holders.length > 1) {
      warnings.push({ kind: 'duplicate-queue-name', printerName: queue, slotIds: holders })
    }
  }

  // Specificity precedence: a claim that named this frame (directly or via a
  // category) beats a catch-all claim. This is what reproduces the legacy
  // first-match-wins: default is allFrames, secondary lists F, so default
  // drops out and F prints on secondary.
  const specific = deduped.filter((c) => c.match !== 'all')

  // Step 1.5 — project binding. Only when NO specific claimant survived: the
  // binding is a default for the project, never an override of an explicit
  // frame/category claim. A binding to a slot with no printer falls through
  // to the existing logic (refuse-never-reroute: Settings warns instead).
  if (specific.length === 0 && options.projectSlot) {
    const bound = ordered.find((s) => s.slotId === options.projectSlot && s.printerName)
    if (bound) {
      return {
        plan: {
          frameId,
          target: {
            slotId: bound.slotId,
            printerName: bound.printerName as string,
            settings: bound.settings,
            match: 'project',
            shared: false
          },
          claimants: claims.map((c) => c.slot.slotId),
          warnings
        },
        nextCursor: cursor
      }
    }
  }

  const candidates = specific.length > 0 ? specific : deduped

  // The default slot is expected in every call (the store always has one);
  // the guard keeps the pure function total rather than throwing in a print path.
  const fallbackTarget = (): PrintTarget => ({
    slotId: 'default',
    printerName: defaultSlot?.printerName || 'DS-RX1',
    settings: defaultSlot ? defaultSlot.settings : ({} as PrintSettings),
    match: 'fallback',
    shared: false
  })

  if (candidates.length === 0) {
    // Nobody claims this frame. Never drop a paid print: the default printer
    // takes it, and Settings shows why.
    warnings.push({ kind: 'unclaimed-frame' })
    return {
      plan: { frameId, target: fallbackTarget(), claimants: [], warnings },
      nextCursor: cursor
    }
  }

  const claimants = claims.map((c) => c.slot.slotId)

  if (candidates.length === 1 || !options.multiClaimRouting) {
    // Single claimant — or multi-claim routing is OFF, in which case overlap
    // resolves by the legacy strict priority (candidates are already in
    // tertiary > secondary > default order).
    return {
      plan: { frameId, target: toTarget(candidates[0], false), claimants, warnings },
      nextCursor: cursor
    }
  }

  // Flag ON and several slots share this frame: round-robin within the group.
  const key = candidates
    .map((c) => c.slot.slotId)
    .sort()
    .join('|')
  const last = cursor[key] ?? -1
  const next = (last + 1) % candidates.length
  return {
    plan: { frameId, target: toTarget(candidates[next], true), claimants, warnings },
    nextCursor: { ...cursor, [key]: next }
  }
}

// ─── Persist migration (v0 → v1) ────────────────────────────────────────────

/**
 * The subset of the persisted print-settings payload the migration touches.
 * Everything else is passed through untouched.
 */
export interface PersistedPrintStateV0 {
  settings?: PrintSettings
  selectedPrinter?: string
  secondaryPrinter?: {
    printerName: string
    settings: PrintSettings
    frameIds: string[]
    purpose?: 'special-frames' | 'parallel'
    routing?: PrinterRouting
  }
  tertiaryPrinter?: {
    printerName: string
    settings: PrintSettings
    frameIds: string[]
    purpose?: 'special-frames' | 'parallel'
    routing?: PrinterRouting
  }
  lastParallelPrinterIndex?: number
  defaultRouting?: PrinterRouting
  roundRobinCursor?: Record<string, number>
  [key: string]: unknown
}

/**
 * v0 → v1: derive explicit routing claims from the legacy fields.
 *
 * Deliberately idempotent (running it on an already-migrated payload is a
 * no-op) and NON-destructive: `frameIds` and `purpose` stay on the slot
 * objects untouched, so downgrading the app restores exact previous behaviour
 * mid-event. The legacy `purpose: 'parallel'` branch in view/Select.tsx is
 * also untouched by Phase 1 — it still reads `purpose` directly — so parallel
 * configs behave identically until Phase 3 retires that branch.
 */
export function migratePrintStateV0toV1(state: PersistedPrintStateV0): PersistedPrintStateV0 {
  const migrated: PersistedPrintStateV0 = { ...state }

  if (!migrated.defaultRouting) {
    migrated.defaultRouting = { allFrames: true, frameIds: [], categories: [] }
  }

  for (const slotKey of ['secondaryPrinter', 'tertiaryPrinter'] as const) {
    const slot = migrated[slotKey]
    if (slot && !slot.routing) {
      migrated[slotKey] = {
        ...slot,
        routing: {
          allFrames: false,
          frameIds: [...(slot.frameIds ?? [])],
          categories: []
        }
      }
    }
  }

  if (!migrated.roundRobinCursor) {
    // Carry the legacy round-robin position into the all-slots group so a
    // booth mid-event doesn't repeat a printer on its next parallel print.
    migrated.roundRobinCursor =
      typeof migrated.lastParallelPrinterIndex === 'number'
        ? { 'default|secondary|tertiary': migrated.lastParallelPrinterIndex }
        : {}
  }

  return migrated
}

// ─── Persist migration (v1 → v2) ────────────────────────────────────────────

/** v2 adds the per-project slot binding map (multi-project hub, plan §4.5). */
export interface PersistedPrintStateV2 extends PersistedPrintStateV0 {
  printerSlotByProject?: Record<string, SlotId>
}

/**
 * v1 → v2: add `printerSlotByProject: {}`. Idempotent and non-destructive;
 * with no binding recorded the resolver ignores the map entirely, so a booth
 * that never opens the new Settings radio behaves exactly as before.
 */
export function migratePrintStateV1toV2(state: PersistedPrintStateV0): PersistedPrintStateV2 {
  const migrated = state as PersistedPrintStateV2
  if (!migrated.printerSlotByProject || typeof migrated.printerSlotByProject !== 'object') {
    return { ...migrated, printerSlotByProject: {} }
  }
  return migrated
}
